import axios from 'axios';

// AI Influencer backend API
const INFLUENCER_API_URL = process.env.REACT_APP_INFLUENCER_API_URL || 'http://localhost:5000';
const INFLUENCER_API_BASE = `${INFLUENCER_API_URL}/api`;

const influencerApi = axios.create({
  baseURL: INFLUENCER_API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach JWT token to every request
influencerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('xalent_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper function to convert character parameters to a natural language prompt
function buildPromptFromParams(params) {
  const {
    gender, age, skinTone, eyeColor, faceStyle, expression,
    hairStyle, outfitStyle, background, lighting, extraNotes,
  } = params;

  const formatValue = (value) => value.replace(/-/g, ' ');

  let prompt = `A ${formatValue(faceStyle)} ${formatValue(expression)} portrait of a ${age}-year-old ${gender} with ${formatValue(skinTone)} skin tone and ${formatValue(eyeColor)} eyes. `;
  prompt += `${formatValue(hairStyle)} hair, wearing ${formatValue(outfitStyle)} attire. `;
  prompt += `${formatValue(background)} background with ${formatValue(lighting)} lighting. `;

  if (extraNotes && extraNotes.trim()) {
    prompt += `Additional details: ${extraNotes}. `;
  }

  prompt += 'Photorealistic, high quality, professional portrait for talking head video with transparent background.';
  return prompt;
}

// Helper to get full image URL from AI Influencer backend
export function getInfluencerImageUrl(path) {
  return `${INFLUENCER_API_URL}${path}`;
}

export const influencerService = {
  // Generate character
  generateCharacter: async (params) => {
    const prompt = buildPromptFromParams(params);
    const response = await influencerApi.post('/generate-character', {
      prompt,
      width: 720,
      height: 1024,
    });
    return response.data;
  },

  // Generate background/environment
  generateBackground: async (prompt, width = 1024, height = 720) => {
    const response = await influencerApi.post('/generate-background', {
      prompt, width, height,
    });
    return response.data;
  },

  // Get character library
  getCharacterLibrary: async () => {
    const response = await influencerApi.get('/library/characters');
    return response.data;
  },

  // Get background library
  getBackgroundLibrary: async () => {
    const response = await influencerApi.get('/library/backgrounds');
    return response.data;
  },

  // Get composed library
  getComposedLibrary: async () => {
    const response = await influencerApi.get('/library/composed');
    return response.data;
  },

  // Get video library
  getVideoLibrary: async () => {
    const response = await influencerApi.get('/library/videos');
    return response.data;
  },

  // Edit image
  editImage: async (imagePath, prompt, strength = 0.8) => {
    const response = await influencerApi.post('/edit-image', {
      image_path: imagePath,
      prompt,
      strength,
    });
    return response.data;
  },

  // Save character
  saveCharacter: async (filename, name) => {
    const response = await influencerApi.post('/save-character', {
      filename, name,
    });
    return response.data;
  },

  // Save environment
  saveEnvironment: async (filename, name) => {
    const response = await influencerApi.post('/save-environment', {
      filename, name,
    });
    return response.data;
  },

  // Replace character (after editing)
  replaceCharacter: async (oldFilename, newPath, name) => {
    const response = await influencerApi.post('/replace-character', {
      old_filename: oldFilename,
      new_path: newPath,
      name,
    });
    return response.data;
  },

  // Replace environment (after editing)
  replaceEnvironment: async (oldFilename, newPath, name) => {
    const response = await influencerApi.post('/replace-environment', {
      old_filename: oldFilename,
      new_path: newPath,
      name,
    });
    return response.data;
  },

  // Compose images
  composeImages: async (characterFilename, backgroundFilename, shrinkPixels = 5) => {
    const response = await influencerApi.post('/compose-image', {
      character: characterFilename,
      background: backgroundFilename,
      shrink_pixels: shrinkPixels,
    });
    return response.data;
  },

  // Generate video from paths (auto-composes images) - standard 10s
  generateVideoFromPaths: async (characterPath, backgroundPath, scenePrompt, duration = 30, dimension = '1280*720') => {
    const response = await influencerApi.post('/generate-video', {
      character_path: characterPath,
      background_path: backgroundPath,
      scene_prompt: scenePrompt,
      duration,
      dimension,
    });
    return response.data;
  },

  // Generate multi-clip video (30s or 40s) using v2 API
  generateVideoV2: async (characterPath, backgroundPath, scenePrompt, duration = 30, dimension = '1280*720') => {
    const response = await influencerApi.post('/generate-video-v2', {
      character_path: characterPath,
      background_path: backgroundPath,
      scene_prompt: scenePrompt,
      duration,
      dimension,
    });
    return response.data;
  },

  // Check job status
  checkJobStatus: async (jobId) => {
    const response = await influencerApi.get(`/job-status/${jobId}`);
    return response.data;
  },

  // Cleanup temp files
  cleanupTempFiles: async (keepFiles = []) => {
    const response = await influencerApi.post('/cleanup-temp', {
      keep: keepFiles,
    });
    return response.data;
  },
};

export default influencerService;
